import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import './ProductEditModal.css'; // Reusing your beautiful modal styles!

const ConsultantEditModal = ({ consultant, isOpen, onClose, onSave, onRemoveFee }) => {
  const getInitialState = () => ({
    email: '',
    displayName: '',
    phone: '',
    profileHeadline: '',
    bio: '',
    experienceYears: 0,
    languages: 'Hindi, English',
    specializations: 'Vastu',
    isActive: true,
    // --- 1. ADD TO INITIAL STATE ---
    defaultUpiId: '', 
    defaultPaymentLink: '', 
    defaultPaymentNotes: ''
  });

  const [formData, setFormData] = useState(getInitialState());
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [defaultQrFile, setDefaultQrFile] = useState(null); // <-- 1. New State
  const [isSaving, setIsSaving] = useState(false);
  const [newFee, setNewFee] = useState({ name: '', price: '', durationMinutes: 45 });

  useEffect(() => {
    if (isOpen) {
      if (consultant) {
        setFormData({
          email: consultant.email || '',
          displayName: consultant.displayName || '',
          phone: consultant.phone || '',
          profileHeadline: consultant.profileHeadline || '',
          bio: consultant.bio || '',
          experienceYears: consultant.experienceYears || 0,
          languages: (consultant.languages || []).join(', '),
          specializations: (consultant.specializations || []).join(', '),
          isActive: consultant.isActive,
          // --- 2. POPULATE FROM CONSULTANT DATA ---
          defaultUpiId: consultant.defaultPaymentInstructions?.upiId || '',
          defaultPaymentLink: consultant.defaultPaymentInstructions?.paymentLink || '',
          defaultPaymentNotes: consultant.defaultPaymentInstructions?.notes || ''
        });
      } else {
        setFormData(getInitialState());
      }
      
      // Reset files on open
      setProfilePhoto(null);
      setDefaultQrFile(null); // <-- 2. Reset
    }
  }, [consultant, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFeeChange = (e) => {
    setNewFee({ ...newFee, [e.target.name]: e.target.value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    const data = new FormData();
    // 3. Object.keys handles all standard fields, including our new default payment fields
    Object.keys(formData).forEach(key => data.append(key, formData[key]));
    
    if (profilePhoto) data.append('profilePhoto', profilePhoto);
    if (defaultQrFile) data.append('defaultQrCode', defaultQrFile); // <-- 3. Append to FormData

    // --- FIX FOR BUG 1 ---
    // If they filled out the new fee section (whether adding OR editing a consultant)
    // append the explicit keys that the parent component is looking for.
    if (newFee.name && newFee.price) {
        data.append('consultationName', newFee.name);
        data.append('consultationPrice', newFee.price);
        // Also pass the duration
        data.append('consultationDuration', newFee.durationMinutes || 45); 
    }
    // -----------------------

    await onSave(data);
    setIsSaving(false);
    
    // Clear the new fee inputs after saving so they don't persist if the modal is reopened
    setNewFee({ name: '', price: '', durationMinutes: 45 });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h3>{consultant ? 'Edit Consultant Profile' : 'Add New Consultant'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            
            {/* Basic Info */}
            <div className="form-row">
              <div className="form-group">
                <label>Anandmaya User Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={!!consultant} required />
                {!consultant && <small>Must be an existing user email.</small>}
              </div>
              <div className="form-group">
                <label>Display Name *</label>
                <input type="text" name="displayName" value={formData.displayName} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Years of Experience</label>
                <input type="number" min="0" name="experienceYears" value={formData.experienceYears} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Languages (comma separated)</label>
                <input type="text" name="languages" value={formData.languages} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Specializations (comma separated)</label>
                <input type="text" name="specializations" value={formData.specializations} onChange={handleChange} />
              </div>
            </div>

            {/* Profile Details */}
            <div className="form-group">
              <label>Profile Headline</label>
              <input type="text" name="profileHeadline" value={formData.profileHeadline} onChange={handleChange} placeholder="e.g. Expert Vastu Consultant for Homes" />
            </div>

            <div className="form-group">
              <label>Detailed Bio</label>
              <textarea name="bio" rows="4" value={formData.bio} onChange={handleChange}></textarea>
            </div>

            <div className="form-group">
              <label>Profile Photo</label>
              <input type="file" accept="image/jpeg, image/png" onChange={(e) => setProfilePhoto(e.target.files[0])} />
              {consultant?.profilePhoto && <small style={{display:'block', marginTop:'5px'}}>Current: {consultant.profilePhoto.originalName}</small>}
            </div>

            <div className="form-group">
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} style={{ width: 'auto' }}/>
                Profile is Active and Visible
              </label>
            </div>

            {/* --- 4. NEW DEFAULT PAYMENT SECTION --- */}
            <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
              <h4 style={{ marginBottom: '1rem', color: 'var(--primary-color)' }}>Default Payment Instructions</h4>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>These will auto-populate for this consultant's new bookings.</p>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Default UPI ID</label>
                  <input type="text" name="defaultUpiId" value={formData.defaultUpiId} onChange={handleChange} placeholder="e.g. name@bank" />
                </div>
                <div className="form-group">
                  <label>Default Payment Link</label>
                  <input type="url" name="defaultPaymentLink" value={formData.defaultPaymentLink} onChange={handleChange} placeholder="https://..." />
                </div>
              </div>
              <div className="form-group">
                <label>Default Notes</label>
                <textarea name="defaultPaymentNotes" rows="2" value={formData.defaultPaymentNotes} onChange={handleChange}></textarea>
              </div>

              {/* --- 4. NEW QR UPLOAD INPUT --- */}
              <div className="form-group" style={{ background: '#fdfdfd', padding: '1rem', border: '1px dashed #ccc', borderRadius: '8px' }}>
                <label>Default QR Code Image</label>
                <input type="file" accept="image/*" onChange={(e) => setDefaultQrFile(e.target.files[0])} />
                
                {/* Show existing QR preview if it exists */}
                {consultant?.defaultPaymentInstructions?.qrCodeFile?.storagePath && (
                   <div style={{ marginTop: '10px' }}>
                     <small style={{ display: 'block', marginBottom: '5px' }}>Current Default QR:</small>
                     <img src={consultant.defaultPaymentInstructions.qrCodeFile.storagePath} alt="Current Default QR" style={{ maxWidth: '150px', border: '1px solid #eee', borderRadius: '4px' }} />
                   </div>
                )}
              </div>
              {/* ------------------------------ */}
            </div>
            {/* -------------------------------------- */}

            {/* --- FEE CATEGORIES SECTION --- */}
            {consultant && (
                <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                    <h4 style={{ marginBottom: '1rem', color: 'var(--primary-color)' }}>Manage Fee Categories</h4>
                    
                    {/* List Existing Fees */}
                    <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1.5rem' }}>
                        {(consultant.consultationTypes || []).map(fee => (
                            <div key={fee._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '6px' }}>
                                <span><strong>{fee.name}</strong> - ₹{fee.price} ({fee.durationMinutes} mins)</span>
                                <button type="button" onClick={() => onRemoveFee(consultant._id, fee._id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}>
                                    <i className="fas fa-trash"></i>
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Add New Fee (Admin will save profile to add this) */}
                    <div className="form-row" style={{ background: '#fefefe', padding: '15px', border: '1px dashed #ccc', borderRadius: '8px' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>New Category Name</label>
                            <input type="text" name="name" value={newFee.name} onChange={handleFeeChange} placeholder="e.g. Follow-up" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Price (₹)</label>
                            <input type="number" name="price" value={newFee.price} onChange={handleFeeChange} />
                        </div>
                        <small style={{ gridColumn: '1 / -1', color: '#666' }}>Fill this out and click 'Save Changes' below to add a new category.</small>
                    </div>
                </div>
            )}
            
            {!consultant && (
                 <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                 <h4 style={{ marginBottom: '1rem', color: 'var(--primary-color)' }}>Initial Fee Category</h4>
                 <div className="form-row">
                    <div className="form-group">
                        <label>Category Name *</label>
                        <input type="text" name="name" value={newFee.name} onChange={handleFeeChange} required placeholder="e.g. House Plan Review" />
                    </div>
                    <div className="form-group">
                        <label>Price (₹) *</label>
                        <input type="number" name="price" value={newFee.price} onChange={handleFeeChange} required />
                    </div>
                </div>
                </div>
            )}

          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsultantEditModal;